//* https://cdn.admeira.ch/doc/docs/tagman-api.html

import config from '@config'
import useAds from '@hooks/useAds'
import useIsInArticleBodyContent from '@hooks/useIsInArticleBodyContent'
import useIsInGalleryContent from '@hooks/useIsInGalleryContent'
import usePageMetadata from '@hooks/usePageMetadata'
import useRiAdTagManagerInit from '@hooks/useRiAdTagManagerInit'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import {
  getCurrentAdViewportType,
  getPageType,
  logAdMessage,
  showSlot,
} from '@utils/ads'
import { extractPageType } from '@utils/cook'
import {
  Fragment,
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import styled, { css } from 'styled-components'
import AdBackgroundWrapper from './AdBackgroundWrapper'
import { PlainAdEvents, PlainAdOptions } from './types'
import { placeholderCSSForPlacement, registerSlot } from './utils'
import { printCSS } from '@utils/style'

export interface Targeting {
  articleid?: string
  articleid_fr?: string
  subsectionid: string[]
  pagetype: 'article' | 'overview'
  syndication?: string
}

export interface AppNexusProps {
  placement: string
  isAdDisabled?: boolean
  slotCode?: string
  slotCodeMobile?: string
  containerId?: string
  autoLoadSlot?: boolean
  onAdLoaded?: Required<PlainAdEvents>['adLoaded']
  onAdEmpty?: Required<PlainAdEvents>['adEmpty']
  onAdError?: Required<PlainAdEvents>['adError']
}

const {
  ads: {
    admeira: {
      appnexus: { articleIdKeyName: appnexusArticleIdKeyName },
    },
  },
} = config

interface StyledAppNexusContainerProps {
  placement: string
  slotCode?: string
  slotCodeMobile?: string
  isArticle: boolean
  hasAdBackground: boolean
}

const StyledAppNexusContainer = styled.aside<StyledAppNexusContainerProps>`
  ${({
    placement,
    slotCode,
    slotCodeMobile,
    isArticle,
    hasAdBackground,
    theme,
  }) => css`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    justify-items: center;

    ${desktopCSS(css`
      ${!slotCode &&
      css`
        display: none;
      `}
    `)}

    ${tabletCSS(css`
      ${!slotCode &&
      css`
        display: none;
      `}
    `)}

    ${mobileCSS(css`
      ${!slotCodeMobile &&
      css`
        display: none;
      `}
    `)}

    ${placeholderCSSForPlacement({
      theme,
      placement,
      slotCode,
      slotCodeMobile,
      isArticle,
      hasAdBackground,
    })}

    ${printCSS(css`
      display: none;
    `)}
  `}
`

const AppNexus: FunctionComponent<AppNexusProps> = ({
  placement,
  isAdDisabled,
  slotCode,
  slotCodeMobile,
  containerId,
  autoLoadSlot,
  onAdLoaded,
  onAdEmpty,
  onAdError,
}) => {
  const tagManagerInitCode = useRiAdTagManagerInit()
  const pageMetadata = usePageMetadata()
  const { syndication, teaser, type, id, sectionIds } = pageMetadata
  const { areAdSlotsLoaded, loadAllSlots } = useAds()
  const isInArticleBody = useIsInArticleBodyContent()
  const isInGallery = useIsInGalleryContent()

  const isArticle = extractPageType(pageMetadata) === 'article'

  const getCurrentSlot = useCallback(
    () =>
      getCurrentAdViewportType() === 'desktop' ? slotCode : slotCodeMobile,
    [slotCode, slotCodeMobile]
  )

  const onLoaded = useCallback<Required<PlainAdEvents>['adLoaded']>(
    (...args) => {
      onAdLoaded?.(...args)
    },
    [onAdLoaded]
  )

  const onEmpty = useCallback<Required<PlainAdEvents>['adEmpty']>(
    (...args) => {
      onAdEmpty?.(...args)
    },
    [onAdEmpty]
  )

  const onError = useCallback<Required<PlainAdEvents>['adError']>(
    (...args) => {
      onAdError?.(...args)
    },
    [onAdError]
  )

  const container = `appnexus-placement-${placement}${
    containerId ? `-${containerId}` : ''
  }`

  const contentType = teaser?.targetContentType

  const hasAdBackground =
    (isInArticleBody || placement === 'recipeAd') &&
    !isInGallery &&
    !getCurrentSlot()?.startsWith('IAV_')

  const registrationOptions = useMemo<Omit<PlainAdOptions, 'slot'>>(
    () => ({
      container,
      targeting: {
        placement,
        pagetype: getPageType(type),
        ...(id ? { [appnexusArticleIdKeyName]: id } : {}),
        ...(contentType ? { contenttype: contentType } : {}),
        ...(syndication ? { syndication } : {}),
        subsectionid: sectionIds,
      },
      events: {
        adLoaded: onLoaded,
        adEmpty: onEmpty,
        adError: onError,
      },
    }),
    [
      container,
      contentType,
      id,
      onEmpty,
      onError,
      onLoaded,
      placement,
      sectionIds,
      syndication,
      type,
    ]
  )

  useEffect(() => {
    const slot = getCurrentSlot()
    if (tagManagerInitCode && slot) {
      registerSlot({ ...registrationOptions, slot })
      logAdMessage(
        `ADS: registered slot for ${getCurrentAdViewportType()} for: ${slot}`,
        registrationOptions
      )

      if (autoLoadSlot) {
        loadAllSlots()
        logAdMessage(
          `ADS: loaded slot manually for ${getCurrentAdViewportType()} for: ${slot}`,
          registrationOptions
        )
        showSlot(container)
        logAdMessage(
          `ADS: Manually showing slot for ${getCurrentAdViewportType()} for: ${slot}`,
          registrationOptions
        )
      }

      if (!autoLoadSlot && areAdSlotsLoaded()) {
        //* Load slot manually
        loadAllSlots()
        logAdMessage(
          `ADS: loaded slot manually for ${getCurrentAdViewportType()} for: ${slot}`,
          registrationOptions
        )
      }
    }
  }, [
    areAdSlotsLoaded,
    autoLoadSlot,
    container,
    getCurrentSlot,
    loadAllSlots,
    registrationOptions,
    tagManagerInitCode,
  ])

  if (isAdDisabled !== false || (!getCurrentSlot() && tagManagerInitCode)) {
    return null
  }

  const Wrapper = hasAdBackground ? AdBackgroundWrapper : Fragment

  return (
    <Wrapper {...(hasAdBackground ? { slotCode, slotCodeMobile } : {})}>
      <StyledAppNexusContainer
        key={`${getCurrentSlot()}-${getCurrentAdViewportType()}`}
        placement={placement}
        slotCode={slotCode}
        slotCodeMobile={slotCodeMobile}
        id={container}
        isArticle={isArticle}
        hasAdBackground={hasAdBackground}
        //TODO: Check if this is still needed after the approval of the new Ad Manager (by Ringier Advertising)
        //! The following property might seem like it is doing nothing.
        //! However, it is important in order to prevent React from discarding
        //! the node's contents once hydration happens on the client.
        //! The contents of this node might change between the Server Side Rendering
        //! and the hydration, due to the registering of the slots that is done before hydration.
        dangerouslySetInnerHTML={{ __html: '' }}
      />
    </Wrapper>
  )
}

const MemoizedAppNexus = memo(AppNexus)

MemoizedAppNexus.displayName = 'MemoizedAppNexus'

export default MemoizedAppNexus
